import React from "react";
import { Updater } from "use-immer";

import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { AnalyticsResourceType, MetricArrayType } from "@intelligems/ig-types";
import { ContentPasteSearch } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../api/configurations/use-configuration";
import {
  canReorderTestGroups,
  shouldShowGraphHeader,
  useGraphValueFormatter,
} from "../../api/formatters/dashboard";
import { VariationOrderPopover } from "../../components/Popover/VariationOrderPopover";
import {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../types/analytics";
import { ExperienceFe } from "../../types/schemas/experiences";
import Loading from "../../utils/Loading";

import { EstImpact } from "./Graphs/Utils/EstImpact";
import { GraphConfig, GraphType } from "./Graphs/Utils/graphConfigs";
import { calculateSumMetric, getImpact } from "./Graphs/Utils/graphHelpers";
import {
  headerFontSize,
  headerFontWeight,
} from "./Graphs/Utils/graphStyleCustoms";
import { VariationFilter } from "./Graphs/Utils/VariationFilter";
import { GraphHeaderDetails } from "./Utils/GraphHeaderDetails";

const PREFIX = "SingleGraphHeader";

const classes = {
  graphHeader: `${PREFIX}-graphHeader`,
  impactNumber: `${PREFIX}-impactNumber`,
  aovFilterSelect: `${PREFIX}-aovFilterSelect`,
  graphHeaderFlex: `${PREFIX}-graphHeaderFlex`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.impactNumber}`]: {
    fontSize: "35px",
    fontWeight: 600,
    letterSpacing: ".15px",
  },
  [`& .${classes.graphHeader}`]: {
    fontWeight: headerFontWeight,
    fontSize: headerFontSize,
  },
  [`& .${classes.aovFilterSelect}`]: {
    height: "25px",
  },
  [`& .${classes.graphHeaderFlex}`]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: "10px",
  },
}));

interface SingleGraphHeaderProps {
  groupMetricData?:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[];
  resource?: ExperienceFe;
  resourceType: AnalyticsResourceType;
  compareGroupA?: string;
  compareGroupB?: string;
  config: GraphConfig;
  aovFilterChoice?: string;
  setAovFilterChoice?: Updater<string>;
  totalTestVisitors?: number;
  graphMetricTotals?: MetricArrayType;
  metricFilterIndex?: number;
  onMoreDetailsClick?: (config: GraphConfig) => void;
  isDrawerRender?: boolean;
}

export const SingleGraphHeader = ({
  groupMetricData,
  compareGroupA,
  compareGroupB,
  config,
  resource,
  resourceType,
  aovFilterChoice,
  setAovFilterChoice,
  totalTestVisitors,
  graphMetricTotals,
  metricFilterIndex,
  onMoreDetailsClick,
  isDrawerRender,
}: SingleGraphHeaderProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );
  const graphValueFormatter = useGraphValueFormatter();

  const canReorderGroups = canReorderTestGroups(config);

  const shouldNotShowHeader = shouldShowGraphHeader(
    shouldShowProfit!.value!,
    config
  );

  const showHistFilter = config.graphType === GraphType.Histogram;
  const showImpact = config.isImpactGraph;

  if (shouldShowProfit?.value !== undefined) {
    return (
      <StyledGrid
        item
        container
        direction={"row"}
        justifyContent={showImpact ? "left" : "space-between"}
        alignItems={"center"}
      >
        <Grid item sx={{ width: "20px" }} />
        {!isDrawerRender && (
          <GraphHeaderDetails
            config={config}
            metricFilterIndex={metricFilterIndex}
          />
        )}

        {graphMetricTotals && resourceType === "campaigns" && (
          <Grid item sx={{ width: "20px" }} />
        )}
        {resourceType === "experiences" && resource ? (
          <Grid item>
            {showHistFilter && aovFilterChoice && setAovFilterChoice ? (
              <VariationFilter
                config={config}
                filterChoice={aovFilterChoice}
                setFilterChoice={setAovFilterChoice}
              />
            ) : canReorderGroups ? (
              <VariationOrderPopover
                id={config.title[metricFilterIndex || 0]}
                experience={resource}
              />
            ) : null}
          </Grid>
        ) : config.showMoreDetails && onMoreDetailsClick && !isDrawerRender ? (
          <Grid item>
            <IgTooltip title={"See more details"}>
              <IconButton
                onClick={() => {
                  onMoreDetailsClick(config);
                }}
              >
                <ContentPasteSearch sx={{ fontSize: "18px" }} />
              </IconButton>
            </IgTooltip>
          </Grid>
        ) : null}
        {!shouldNotShowHeader ? (
          <>
            {config.showSumMetric && graphMetricTotals && (
              <Grid item container direction={"row"} justifyContent={"center"}>
                {(config.sumMetrics ?? config.metrics).map((_metric, index) => {
                  let metric: string | undefined;
                  if (_metric?.key) {
                    metric = _metric.key;
                  } else {
                    metric = _metric;
                  }
                  return (
                    <Grid item key={index}>
                      {index > 0 ? <IgTypography>&nbsp;(</IgTypography> : null}
                      <IgTypography
                        sx={{
                          fontWeight: 600,
                          fontSize:
                            resourceType === "sitewide" ? "15px" : "25px",
                        }}
                      >
                        {calculateSumMetric(
                          config,
                          graphMetricTotals,
                          graphValueFormatter,
                          metric as string
                        )}

                        {config.sumMetrics && (
                          <IgTypography
                            sx={{
                              fontWeight: 600,
                              fontSize:
                                resourceType === "sitewide" ? "15px" : "25px",
                            }}
                          >
                            {" "}
                            {_metric?.label}
                          </IgTypography>
                        )}
                        {index > 0 && ")"}
                      </IgTypography>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {groupMetricData &&
            showImpact &&
            totalTestVisitors &&
            compareGroupB &&
            compareGroupA ? (
              <Grid container direction={"column"}>
                <Grid
                  sx={{ marginLeft: "5px" }}
                  container
                  direction={"row"}
                  columnSpacing={1}
                  alignItems={"center"}
                  textAlign={"center"}
                >
                  <Grid item>
                    {getImpact(
                      groupMetricData as BarMetricData[],
                      true,
                      compareGroupA,
                      compareGroupB,
                      storeCurrencyConfig?.value?.locale
                    )}
                  </Grid>
                  <Grid item>
                    <IgTypography className={classes.impactNumber}>
                      {getImpact(
                        groupMetricData as BarMetricData[],
                        false,
                        compareGroupA,
                        compareGroupB,
                        storeCurrencyConfig?.value?.locale
                      )}
                    </IgTypography>
                  </Grid>
                </Grid>
                <Grid
                  sx={{ marginLeft: "10px" }}
                  container
                  direction={"row"}
                  columnSpacing={1}
                  alignItems={"center"}
                  textAlign={"center"}
                >
                  <EstImpact
                    groupMetricData={groupMetricData}
                    compareGroupB={compareGroupB}
                    compareGroupA={compareGroupA}
                    config={config}
                    totalTestVisitors={totalTestVisitors}
                  />
                </Grid>
              </Grid>
            ) : null}
          </>
        ) : (
          <Grid item />
        )}
      </StyledGrid>
    );
  }
  return <Loading />;
};
